import React from 'react';
import {Tag, Tooltip} from 'antd';
import {BranchesOutlined, EllipsisOutlined, RobotOutlined,} from '@ant-design/icons';
import {TrackEvolutionEvent, TrackEvolutionPage} from './trackEvolutionTypes';
import {runStatusColor} from './TrackEvolutionEventCard';

interface TrackEvolutionTimelineProps {
    events: TrackEvolutionEvent[];
    page: TrackEvolutionPage | null;
    selectedEventKey: string | null;
    focusRunId: number | null;
    onSelectEvent: (eventKey: string) => void;
    onLoadOlder: () => void;
    loadingOlder: boolean;
}

const STATUS_HEX: Record<string, string> = {
    success: 'var(--pt-bull)',
    error: 'var(--pt-bear)',
    warning: 'var(--pt-amber)',
    processing: 'var(--pt-blue)',
    default: 'rgba(255, 255, 255, 0.65)',
};

function formatSlotTime(ms: number): string {
    const d = new Date(ms);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString('zh-CN', {hour: '2-digit', minute: '2-digit'})}`;
}

function eventKey(ev: TrackEvolutionEvent): string {
    return ev.eventType === 'ORIGINAL_DECISION'
        ? `ORIGINAL:${ev.decision?.decisionId ?? 'unknown'}`
        : `TRACK:${ev.run?.runId ?? 'unknown'}`;
}

const TrackEvolutionTimeline: React.FC<TrackEvolutionTimelineProps> = ({
                                                                           events,
                                                                           page,
                                                                           selectedEventKey,
                                                                           focusRunId,
                                                                           onSelectEvent,
                                                                           onLoadOlder,
                                                                           loadingOlder,
                                                                       }) => {
    const sorted = [...events].sort((a, b) => a.eventTimeMs - b.eventTimeMs);

    return (
        <div className="track-evolution-timeline">
            {sorted.map(ev => {
                const key = eventKey(ev);
                const isOrigin = ev.eventType === 'ORIGINAL_DECISION';
                const isSelected = key === selectedEventKey;
                const isFocus = ev.run?.runId === focusRunId;
                const statusColor = runStatusColor(ev.run?.status ?? '');
                const statusHex = STATUS_HEX[statusColor] ?? STATUS_HEX.default;
                const failed = ev.run?.status.includes('FAILED') === true;
                const inputVersion = ev.run?.inputVersionNo ?? null;
                const outputVersion = ev.run?.outputVersionNo ?? null;
                const showVersion = !isOrigin && ev.run && (inputVersion != null || outputVersion != null);
                const versionFailed = failed && outputVersion == null;
                return (
                    <div
                        key={key}
                        className={`track-evolution-timeline-node${isSelected ? ' selected' : ''}${isFocus ? ' focus' : ''}`}
                        onClick={() => onSelectEvent(key)}
                    >
                        <div className="track-evolution-timeline-node-icon">
                            {isOrigin ? (
                                <BranchesOutlined style={{color: 'var(--pt-blue)', fontSize: 14}}/>
                            ) : (
                                <Tooltip title={ev.run?.statusLabel ?? 'Track'}>
                                    <RobotOutlined style={{color: statusHex, fontSize: 14}}/>
                                </Tooltip>
                            )}
                        </div>
                        <div className="track-evolution-timeline-node-time">
                            {formatSlotTime(ev.eventTimeMs)}
                        </div>
                        {isOrigin && (
                            <div className="track-evolution-timeline-node-label">
                                原始决策
                            </div>
                        )}
                        {showVersion && (
                            <div className="track-evolution-timeline-node-version"
                                 style={{color: versionFailed ? 'var(--pt-bear)' : undefined}}>
                                V{inputVersion ?? '-'}→{versionFailed ? '×' : `V${outputVersion ?? '-'}`}
                            </div>
                        )}
                        {isFocus && (
                            <Tag className="track-evolution-timeline-node-tag" color="cyan"
                                 style={{fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0}}>
                                当前查看
                            </Tag>
                        )}
                    </div>
                );
            })}
            {page?.hasMore && (
                <div className="track-evolution-timeline-gap">
                    <div className="track-evolution-timeline-gap-line"/>
                    <Tooltip title="加载更早记录">
            <span
                style={{cursor: 'pointer', color: loadingOlder ? undefined : 'var(--pt-primary)'}}
                onClick={loadingOlder ? undefined : onLoadOlder}
            >
              <EllipsisOutlined/>
            </span>
                    </Tooltip>
                    <div className="track-evolution-timeline-gap-line"/>
                </div>
            )}
        </div>
    );
};

export default TrackEvolutionTimeline;
